import { Button } from 'react-bootstrap';
import styles from '../css/ServiceManager.module.css';
import type {
  ActionButtonConfig,
  ServiceAction,
  ServiceStatus,
} from '../types/ServiceManager.types';

interface ActionButtonsProps {
  status?: ServiceStatus;
  /**
   * The supervisor watches this service but never starts or stops it
   * (`control: 'observe'`), so only Restart is offered. Restart is safe on a
   * process we do not own: it acts on something already running, so it can
   * neither resurrect a deliberately stopped service nor start a duplicate.
   */
  restartOnly?: boolean;
  onChange?: (action: ServiceAction) => void;
}

/**
 * Start / stop / restart buttons for a service row. Which buttons appear
 * depends on the current status, and on whether the supervisor will act on
 * this service at all.
 */
export default function ActionButtons({
  status = 'Unknown',
  restartOnly = false,
  onChange = () => {},
}: ActionButtonsProps) {
  const isRunning = status === 'Running' || status === 'Restarting';
  const isStopped = status === 'Stopped';

  const buttons: ActionButtonConfig[] = [
    { action: 'start', label: 'Start', hidden: restartOnly || isRunning, disabled: false },
    { action: 'stop', label: 'Stop', hidden: restartOnly || isStopped, disabled: false },
    { action: 'restart', label: 'Restart', hidden: false, disabled: isStopped },
  ];

  const visibleButtons = buttons.filter((b) => !b.hidden);

  const actionColorClass: Record<ServiceAction, string> = {
    start: styles.actionStart,
    stop: styles.actionStop,
    restart: styles.actionRestart,
  };

  const getButtonClassName = (index: number) => {
    const classes = [styles.actionButton];
    if (index === 0) classes.push(styles.actionButtonFirst);
    if (index === visibleButtons.length - 1) classes.push(styles.actionButtonLast);
    if (index !== 0) classes.push(styles.actionButtonMiddle);
    return classes.join(' ');
  };

  return (
    <div className="d-flex">
      {visibleButtons.map((btn, index) => (
        <Button
          key={btn.action}
          size="sm"
          // Deliberately not `dark` or `secondary`: global.scss themes both to
          // row-coloured tokens (.btn-dark IS --color-primary-shadow, the blue
          // stripe). `outline-secondary` is untouched by the theme, so the
          // module CSS owns the surface outright.
          variant="outline-secondary"
          disabled={btn.disabled}
          className={`flex-fill ${
            btn.disabled ? styles.actionButtonDisabled : actionColorClass[btn.action]
          } ${getButtonClassName(index)}`}
          onClick={(e) => {
            e.stopPropagation();
            onChange(btn.action);
          }}
        >
          {btn.label}
        </Button>
      ))}
    </div>
  );
}
